import { Validation } from './Validation';
import DateFormatter from './DateFormatter';
import { EventStatus } from './EventStatus';
import CategoryTypeJson, { validateCategoryTypeJson } from './CategoryTypeJson';
import EventDateJson, { validateEventDateJson } from './EventDateJson'
import ClientJson, { validateClientJson } from './ClientJson';
import PriceJson, { defaultPriceJson, validateEventPriceJson, validateEventPriceJsonAddNew } from './PriceJson';
import CustomInputJson, { validateCustomInputJson } from "./CustomInputJson";
import EventBookingJson from './EventBookingJson';
import EventImageJson, { validateEventImageJsonAdd, validateEventImageJson } from './EventImageJson';
import EventTemplateJson from './EventTemplateJson';
import { EventTemplateStatus } from './EventTemplateStatus';

/**
 * Angebotene Veranstaltung
 */
export default interface EventJson {
   ID: number | null,

   /**
    * Titel der Veranstaltung.
    */
   title: string,

   /**
    * Status der Veranstaltung.
    */
   state: EventStatus;

   /**
    * Internet Anmerkungen zu der Veranstaltung. Kann leer sein.
    */
   remarks: string;

   /**
    * Ordnet die Veranstaltung in Kategorien ein. Kann null sein, wenn keine Einordnung vorliegt.
    */
   categories: CategoryTypeJson[] | null;

   /**
    * Beschreibung der Veranstaltung.
    */
   description: string;

   /**
    * Bilder zur Veranstaltung. Max 5 Bilder.
    */
   images: EventImageJson[];

   /**
    * Diese Veranstaltung ist intern und nur fuer angemeldete User sichtbar.
    * Nicht genutzt -> Soll über categories abgedeckt werden.
    */
   internalEvent: boolean;

   /**
    * Gibt Kategorien an, auf die diese Veranstaltung beschränkt ist. Kann null sein, wenn keine Beschränkung vorliegt.
    */
   restrictToTypes: CategoryTypeJson[] | null;

   /**
    * Bei minderjährigen Teilnehmende muss eine erziehungsberechtigte Person angegeben werden.
    */
   needsGuardianForUnderaged: boolean;

   /**
     * Kontaktperson, die zuständig für diese Veranstaltung ist.
     */
   contactPerson: ClientJson | null;

   /**
    * Termine an denen die Veranstaltung stattfindet. Bei null handelt es sich um eine Veranstaltung bei der ein Termin angefragt werden kann.
    */
   dates: EventDateJson[] | null;

   /**
    * Liste mit Preiskategorien. 
    */
   prices: PriceJson[];

   /**
    * Liste mit zusätzlichen Kosten/Optionen zum Auswählen. 
    */
   addonPrices: PriceJson[];

   /**
    * Gibt an, ob diese Veranstaltung eine Anmeldung benötigt.
    */
   needsRegistration: boolean;

   /**
    * Gibt an, ob diese Veranstaltung auch außerhalb des Registrierungszeitraums angezeigt werden soll.
    */
   showOutsideRegistration: boolean;

   /**
    * Gibt an, ob die Registrierungen für diese Veranstaltung fregegeben sind. 
    * Wenn es auf true ist, werden immer noch registrationBeginDate und registrationEndDate sowie registrationMaximumCount beachtet.
    */
   allowRegistration: boolean;

   /**
    * Datum und Uhrzeit ab dem Registrierungen möglich sind.
    */
   registrationBeginDateTime: string;

   /**
    * Datum und Uhrzeit bis zu dem Registrierungen möglich sind.
    */
   registrationEndDateTime: string;

   /**
    * Minimale Anzahl an Teilnehmern die benötigt wird.
    */
   registrationMinimumCount: number;

   /**
    * Maximale Anzahl an Teilnehmern die akzeptiert werden.
    */
   registrationMaximumCount: number;

   /**
    * Maximale Anzahl an Teilnehmern die in der Warteliste akzeptiert werden. Kann 0 sein.
    */
   waitingListMaximumCount: number;

   /**
    * Gibt für das Kunden-Buchungsformular an, welches Mindestalter die Teilnehmenden für die Veranstaltung haben sollten. Kann null sein, falls keine Einschränkung besteht.
    */
   restrictMinimumAge: number | null;

   /**
    * Gibt für das Kunden-Buchungsformular an, welches Maximalalter die Teilnehmenden für die Veranstaltung haben sollten. Kann null sein, falls keine Einschränkung besteht.
    */
   restrictMaximumAge: number | null;

   /**
    * Zum Erstellen individueller Form Inputs
    */
   customInputs: CustomInputJson[];
}
export type EventWithBookingsJson = EventJson & { "bookings": EventBookingJson[]; };
export type EventWithBookingJson = EventJson & { "booking": EventBookingJson; };
export type EventsInfoJson = EventJson & { "registrationSlotsFree": number, "waitingListSlotsFree": number; };

export const EventDescriptionAllowedTags: string[] = ["p", "br", "b", "ul", "li", "u", "a"];

export function defaultEventJson(): EventJson {
   return {
      ID: null,
      title: "",
      state: EventStatus.displayed,
      remarks: "",
      categories: null,
      description: "",
      images: [],
      internalEvent: false,
      restrictToTypes: null,
      needsGuardianForUnderaged: true,
      contactPerson: null,
      dates: null,
      prices: [{ ...defaultPriceJson() }],
      addonPrices: [],
      needsRegistration: false,
      showOutsideRegistration: false,
      allowRegistration: false,
      registrationBeginDateTime: "",
      registrationEndDateTime: "",
      registrationMinimumCount: 1,
      registrationMaximumCount: 10,
      waitingListMaximumCount: 0,
      restrictMinimumAge: null,
      restrictMaximumAge: null,
      customInputs: [],
   };
}

export function getEventStatus(event: EventJson): {bookable: boolean, text: string} {
   if (event.state == EventStatus.canceled) {
      return {
         bookable: false,
         text: "Termin wurde abgesagt"
      }
   } else if (!event.needsRegistration) {
      return {
         bookable: false,
         text: "Für diese Veranstaltung wird keine Anmeldung benötigt"
      }
   } else if (event.allowRegistration) {
      let today = new Date();

      if (today < new Date(event.registrationBeginDateTime)) {
         return {
            bookable: false,
            text: "Freigegeben und buchbar ab " + DateFormatter.formatDateTime(new Date(event.registrationBeginDateTime))
         };
      } else if (today > new Date(event.registrationEndDateTime)) {
         return {
            bookable: false,
            text: "Buchungszeitraum abgelaufen"
         };
      } else {
         return {
            bookable: true,
            text: "Freigegeben zur Buchung"
         };
      }
   } else {
      return {
         bookable: false,
         text: "Nicht freigegeben"
      }
   }
}

export function getEventJsonFromTemplateJson(json: EventTemplateJson): EventJson {
   function setIdsNull<T extends { ID: number | null}>(items: T[]): T[] {
      for(let item of items) {
         item.ID = null;
      }
      return items;
   }
   
   return {
      ...defaultEventJson(),
      title: json.eventTitle,
      description: json.eventDescription,
      images: setIdsNull(json.eventImages),
      internalEvent: json.eventInternal,
      restrictToTypes: json.eventRestrictToTypes,
      prices: setIdsNull(json.eventPrices),
      addonPrices: setIdsNull(json.eventAddonPrices),
      registrationMinimumCount: json.eventRegistrationMinimumCount,
      registrationMaximumCount: json.eventRegistrationMaximumCount,
      waitingListMaximumCount: json.eventWaitingListMaximumCount,
      restrictMinimumAge: json.eventRestrictMinimumAge,
      restrictMaximumAge: json.eventRestrictMaximumAge,
      customInputs: json.eventCustomInputs
   };
}

export function getDuplicateEventJson(json: EventJson): EventJson {
   function setIdsNull<T extends { ID: number | null}>(items: T[]): T[] {
      for(let item of items) {
         item.ID = null;
      }
      return items;
   }
   
   return {
      ...json,
      ID: null,
      title: json.title + ' Kopie',
      images: setIdsNull(json.images),
      prices: setIdsNull(json.prices),
      addonPrices: setIdsNull(json.addonPrices),
      allowRegistration: false,
   };
}

export function validateEventJsonManagementAdd(item: EventJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateNonEmptyString("title", 100)
      .validateString("remarks", 500)
      .validateAllUsingFunction("categories", validateCategoryTypeJson, 0, true)
      .validateString("description", 1500)
      .validateAllUsingFunction("images", validateEventImageJsonAdd, 0, false, 5)
      .validateAllUsingFunction("restrictToTypes", validateCategoryTypeJson, 0, true)
      .validateUsingFunction("contactPerson",validateClientJson, true)
      .validateAllUsingFunction("dates", validateEventDateJson, 1, true)
      .validateAllUsingFunction("prices", validateEventPriceJsonAddNew, 1)
      .validateAllUsingFunction("addonPrices", validateEventPriceJsonAddNew)
      .validateDate("registrationBeginDateTime")
      .validateDateLater("registrationEndDateTime", "registrationBeginDateTime")
      .validateInteger("registrationMinimumCount", 0)
      .validateInteger("registrationMaximumCount", 0)
      .validateInteger("waitingListMaximumCount", 0)
      .validateInteger("restrictMinimumAge", 0, 120, true)
      .validateInteger("restrictMaximumAge", 0, 120, true)
      .validateAllUsingFunction("customInputs", validateCustomInputJson)
      .finish();
}

export function validateEventJsonManagement(item: EventJson) {
   return new Validation(item)
      .validateInteger("ID", 0)
      .validateNonEmptyString("title", 100)
      .validateString("remarks", 500)
      .validateAllUsingFunction("categories", validateCategoryTypeJson, 0, true)
      .validateString("description", 1500)
      .validateAllUsingFunction("images", validateEventImageJson, 0, false, 5)
      .validateAllUsingFunction("restrictToTypes", validateCategoryTypeJson, 0, true)
      .validateUsingFunction("contactPerson",validateClientJson, true)
      .validateAllUsingFunction("dates", validateEventDateJson, 1, true)
      .validateAllUsingFunction("prices", validateEventPriceJson, 1)
      .validateAllUsingFunction("addonPrices", validateEventPriceJson)
      .validateDate("registrationBeginDateTime")
      .validateDateLater("registrationEndDateTime", "registrationBeginDateTime")
      .validateInteger("registrationMinimumCount", 0)
      .validateInteger("registrationMaximumCount", 0)
      .validateInteger("waitingListMaximumCount", 0)
      .validateInteger("restrictMinimumAge", 0, 120, true)
      .validateInteger("restrictMaximumAge", 0, 120, true)
      .validateAllUsingFunction("customInputs", validateCustomInputJson)
      .finish();
}